import React, { useState } from 'react';
import { func, object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { manageDisableScrolling } from '../../ducks/ui.duck';
import { createBillingPortalSession } from '../../util/api';
import { HANDOFF_REASON } from '../../util/cfHandoff';
import Modal from '../Modal/Modal';
import NamedLink from '../NamedLink/NamedLink';

import css from '../../containers/EditListingPage/TierUpgradeModal/TierUpgradeModal.module.css';

// ---------------------------------------------------------------------------
// Story 3.16 AC2 — THE INFORMED-CHOICE ASK SCREEN.
//
// Shown when a seller heading for ClickFunnels checkout ALREADY HOLDS a paid
// listing they have never posted. Real production casualties this exists for:
// Rick D paid $214.96 for one horse; Serena H $254.98 with nothing published;
// mphorses21 four subscriptions for three listings.
//
// AC2.1 — visual language is inherited VERBATIM from TierUpgradeModal.module.css
// (cream-soft container, 14px radius, 48px maroon-gradient tile with gold glyph,
// gold-gradient primary / gold-outline secondary, 11px CTA radius). NO NEW
// TOKENS, NO NEW VISUAL IDIOM. Approved reference:
// design-reference/idle-slot-ask-modal.html (operator-approved 2026-08-10, P-2).
//
// ⚠️ COPY IS LOCKED. Render it; do not rewrite it. Two vocabulary rules the
// operator set and the reference restates:
//   - "PAID FOR A LISTING", never "credit" and never "slot". Sellers have no
//     mental model for a slot; they know they paid and nothing is posted.
//     Internal vocabulary stays internal.
//   - THE WORD "DRAFT" NEVER APPEARS. Decision A scoped this modal strictly to
//     the UNUSED PAID LISTING. Story 12.12's DraftResumeCard owns the
//     unfinished-draft case and the two must not be conflated — a seller with a
//     draft and no idle listing must never see this screen.
//
// AC2.6 — the readout names a TIER and a COUNT, never a horse (an idle slot is
// `listing_uuid IS NULL` by definition, so which horse it belongs to does not
// exist as a fact), and never a dollar amount (nothing here can contradict
// Stripe).
// ---------------------------------------------------------------------------

const TIER_LABEL = { basic: 'Basic', standard: 'Standard', premium: 'Premier' };

// The reference's `{ordinal}` rule: the horse the seller would be paying for
// NEXT, spelled out to five, then "another horse". Count 1 is not in this map —
// the singular variant has its own approved phrasing ("two separate horses").
const NEXT_HORSE_ORDINAL = { 2: 'third', 3: 'fourth', 4: 'fifth' };

const ReceiptGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />
    <path d="M9 8h6M9 12h6" />
  </svg>
);

/**
 * AC2.6 — "2 Standard · 1 Premier", built from the AUTHORITATIVE read only.
 * Single-tier singular renders "1 Standard listing" per the reference.
 */
const tierBreakdownLabel = (tierBreakdown, idleSlots) => {
  const parts = (tierBreakdown || []).map(t => `${t.slots} ${TIER_LABEL[t.tier] || t.tier}`);
  if (idleSlots === 1 && parts.length === 1) {
    return `${parts[0]} listing`;
  }
  return parts.join(' · ');
};

/**
 * AC4.2 — Arm B, "your payment needs attention".
 *
 * ⚠️ PER-STATUS, NOT FLATTENED, AND THE LIVE VOCABULARY IS **TWO** STATUSES:
 *   `past_due` — a dunning problem the seller fixes themselves in the billing
 *                portal.
 *   `unpaid`   — dunning is EXHAUSTED. This needs an operator, not a retry.
 * `incomplete` was removed by Story 3.17's BLOCKER B-1 (the first invoice was
 * never paid, so it is not a "payment needs attention" state at all).
 * `paused` and `trialing` are UNREACHABLE in this codebase and marked RESERVED
 * in sellerAvailableTiers.js — writing copy for them would ship dead strings in
 * four locales. DO NOT ADD THEM.
 *
 * 🔴 FLAGGED FOR OPERATOR — ONE APPROVED COPY BLOCK, TWO STATUSES.
 * P-3 was closed with a single approved block, and its answer ("Update your
 * payment method and your listing is ready to use again") is the `past_due`
 * answer. `unpaid` currently renders that same block, which tells a
 * dunning-exhausted seller to retry a card that will not help them. The map
 * below is keyed per status SO THE SECOND STRING CAN DROP IN WITHOUT A
 * REFACTOR. No copy has been invented here — see story §8 P-3.
 */
const ARM_B_MESSAGE_KEYS = {
  past_due: {
    title: 'CheckoutHandoffModal.armB.title',
    body: 'CheckoutHandoffModal.armB.body',
  },
  unpaid: {
    title: 'CheckoutHandoffModal.armB.title',
    body: 'CheckoutHandoffModal.armB.body',
  },
};

/**
 * 🔴 COLD-REVIEW SF-7 — AN UNMAPPED STATUS MUST NOT INHERIT A CARD-DECLINED CLAIM.
 *
 * This used to end `|| ARM_B_MESSAGE_KEYS.past_due`, so ANY status without an
 * entry silently rendered "Your card didn't go through on your listing
 * subscription". `sellerAvailableTiers.js` selects four statuses
 * (`past_due`, `unpaid`, `paused`, `trialing`), and the two unmapped ones are
 * RESERVED-but-unreachable rather than impossible — the query actively asks for
 * them. A seller on a healthy trial being told their card was declined, and
 * sent to the billing portal to fix nothing, is a false statement about money.
 *
 * Returns `null` when we hold no copy that is TRUE for the status in hand.
 *
 * ---------------------------------------------------------------------------
 * 🔴🔴 RE-REVIEW FR-B1 — READ THIS BEFORE TOUCHING `isArmB`.
 * ---------------------------------------------------------------------------
 * The first version of this fix wired `isArmB = reason === ARM_B && !!armB`,
 * i.e. it made ARM-B MEMBERSHIP DEPEND ON WHETHER A COPY STRING EXISTED. The
 * comment here claimed the fallback "reuses the AC2 ask screen, which is
 * already approved and already correct". THAT CLAIM WAS WRONG, and it was the
 * whole defect: the AC2 ask screen carries **"I have another horse — buy a
 * second listing"**, which navigates to the live ClickFunnels funnel. So a
 * seller the gate had just identified as ALREADY PAID was shown a purchase
 * button, lost [Update payment] entirely, and got a wizard route with zero
 * spendable listings — while tph-api posted "stopped a second charge" about a
 * screen that was offering one.
 *
 * ⚠️ ARM B MEMBERSHIP IS DECIDED BY THE STATUS SET, NEVER BY COPY AVAILABILITY.
 * A missing string is a COPY gap, not a classification change. Missing copy
 * degrades WHAT WE SAY inside the Arm B state; it must never change WHICH
 * state renders, because the other state sells something.
 */
const armBKeysFor = statuses => {
  const first = (statuses || []).find(s => ARM_B_MESSAGE_KEYS[s]);
  return first ? ARM_B_MESSAGE_KEYS[first] : null;
};

/**
 * @param {Object} props
 * @param {Object|null} props.handoff the resolved gate result, or null when closed
 * @param {Function} props.onClose cancels the handoff (AC2.5)
 * @param {Function} [props.onOutcome] AC5 recorder; fire-and-forget
 */
const CheckoutHandoffModal = props => {
  const {
    handoff,
    onClose,
    onOutcome = () => {},
    // Mockable navigation seam (same reason as CheckoutHandoffLink's): jsdom
    // will not let window.location be replaced, so AC2.4's "goes to the funnel
    // bare" and AC2.5's "dismiss never falls through" would both be unassertable.
    navigateExternal = url => window.location.assign(url),
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const onManageDisableScrolling = (id, disable) => dispatch(manageDisableScrolling(id, disable));

  const [portalInProgress, setPortalInProgress] = useState(false);
  const [portalError, setPortalError] = useState(false);

  const isOpen = !!handoff;
  const statuses = (handoff?.paidNotSpendable || []).map(r => r.status);

  // ⚠️ TWO COUNTS, AND THEY MUST NOT BE CONFLATED.
  //
  // `spendableSlots` is the authoritative "you can use this RIGHT NOW" count.
  // It is the ONLY thing allowed to offer the wizard route (SF-6), because
  // routing a seller to the wizard with nothing spendable would dead-end them.
  //
  // `displayCount` is what the copy names. On an Arm B fallback (SF-7) the
  // spendable count is 0 by definition, and rendering "you've already paid for
  // 0 listings" would repeat exactly the falsehood FIX-3 removed — so the copy
  // falls back to the paid-but-not-spendable rows, which ARE what the seller
  // paid for. Same screen, two different questions.
  const spendableSlots = handoff?.idleSlots || 0;
  const paidNotSpendableSlots = (handoff?.paidNotSpendable || []).reduce(
    (n, r) => n + (Number(r?.slots) || 0),
    0
  );
  const idleSlots = spendableSlots || paidNotSpendableSlots || 0;
  // FR-4 — computed once; empty means "no tiers to name", which suppresses the
  // readout line entirely rather than rendering a dangling fragment.
  const breakdownLabel = tierBreakdownLabel(handoff?.tierBreakdown, idleSlots);
  // FR-B1 — membership from the STATUS SET (the gate's reason), never from copy
  // availability. `armB` may legitimately be null; that degrades what we SAY,
  // not which state renders. See the armBKeysFor header for what went wrong.
  const isArmB = handoff?.reason === HANDOFF_REASON.ARM_B_PAID_NOT_SPENDABLE;
  const armB = armBKeysFor(statuses);

  // AC2.5 — dismiss (✕, backdrop, Esc) CANCELS THE HANDOFF. A dismissed modal
  // must NEVER fall through to checkout. This is the fenced failure mode (RP-2):
  // there is deliberately no navigation of any kind on this path.
  const handleDismiss = () => {
    onOutcome({ outcome: 'dismissed', handoff });
    onClose();
  };

  // AC2.4 — "I have another horse" proceeds to the funnel EXACTLY as today:
  // the bare URL, nothing appended. Decision D — there is NO forward-binding to
  // ClickFunnels (CF 2.0 param passthrough is dead, settled 2026-08-06, which
  // is precisely why Moves 1/1.5/2 exist). Story 3.11's confirmation-page
  // handback catches them on return.
  const handleBuyAnother = () => {
    onOutcome({ outcome: 'buy_another', handoff });
    navigateExternal(handoff.url);
  };

  // AC2.3 — the primary routes to the in-app wizard. NO CHECKOUT, NO CHARGE.
  // The wizard's existing Case B/C tier logic auto-assigns a single listing or
  // shows the picker for several, so nothing about tier selection is duplicated
  // here.
  //
  // 🔴 COLD-REVIEW SF-14 — the wrapper is WIDER than the link it wraps.
  // `NamedLink` drops `onClick` (it rebuilds a fixed prop whitelist), so the
  // outcome is recorded on a span that catches the BUBBLED click. That span's
  // box includes padding around the anchor, and it caught modifier-clicks too —
  // so a ⌘-click (new tab) or a few pixels off the button recorded
  // `use_existing` and CLOSED the modal without navigating anywhere. The seller
  // ended up where they started with no explanation, and the telemetry
  // over-counted the single outcome this story most wants to be true.
  //
  // Guard on the same two questions the anchor gate asks: was this a plain left
  // click, and did it actually land on the link?
  //
  // ⚠️ NOT `isPlainLeftClick` here, deliberately. That helper also rejects an
  // already-`defaultPrevented` event, which is exactly what a routed
  // `NamedLink` produces — React Router calls preventDefault to navigate
  // client-side. Using it would reject every successful click. The two
  // questions that actually matter for this wrapper are the modifier keys and
  // whether the click landed on the link at all.
  const handleUseExisting = event => {
    if (event) {
      const openedInNewTab =
        event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (openedInNewTab) {
        // The wizard opens in a new tab; this modal has NOT been resolved, so
        // it stays open and nothing is recorded.
        return;
      }
      const onTheLink = event.target && event.target.closest && event.target.closest('a');
      if (!onTheLink) {
        // Landed on the wrapper's padding, not the link. Not a choice.
        return;
      }
    }
    onOutcome({ outcome: 'use_existing', handoff });
    onClose();
  };

  // AC4.2 Arm B — [Update payment] REUSES THE EXISTING STRIPE BILLING-PORTAL
  // ENTRY POINT. `createBillingPortalSession` (src/util/api.js:291) POSTs
  // same-origin to /api/billing/portal-session, which server/api-util/
  // tphBillingProxy.js forwards to tph-api's requireCustomerToken-gated
  // POST /api/v1/billing/portal-session. Shipped by Story 3.8 (AC7) and already
  // driven by BillingSubscriptionsPage's "Manage billing" CTA — this is the
  // same call, deliberately NOT a second path.
  const handleUpdatePayment = () => {
    if (portalInProgress) {
      return;
    }
    setPortalInProgress(true);
    setPortalError(false);
    onOutcome({ outcome: 'blocked', handoff });

    createBillingPortalSession()
      .then(response => {
        const url = response?.url;
        if (!url) {
          throw new Error('Billing portal session response did not include a url');
        }
        navigateExternal(url);
      })
      .catch(() => {
        setPortalError(true);
        setPortalInProgress(false);
      });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      id="CheckoutHandoffModal"
      className={css.modal}
      containerClassName={css.container}
      contentClassName={css.content}
      isOpen={isOpen}
      onClose={handleDismiss}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.header}>
        <span className={css.lock} aria-hidden="true">
          <ReceiptGlyph />
        </span>
        <div>
          {/* FR-B1 — three cases, not two. Arm B WITH copy says why; Arm B
              WITHOUT copy says only what is true (they have paid) and stays
              silent on the reason; everything else is the AC2 ask screen.
              The unmapped case borrows the APPROVED already-paid headline,
              which is true for any paid-not-spendable seller, and OMITS the
              body rather than assert something we cannot support. No copy is
              invented here — see the flag below. */}
          <h3 className={css.head}>
            {isArmB && armB ? (
              <FormattedMessage id={armB.title} />
            ) : (
              <FormattedMessage id="CheckoutHandoffModal.title" values={{ count: idleSlots }} />
            )}
          </h3>
          {isArmB && !armB ? null : (
            <p className={css.sub}>
              {isArmB ? (
                <FormattedMessage id={armB.body} />
              ) : (
                <FormattedMessage id="CheckoutHandoffModal.body" values={{ count: idleSlots }} />
              )}
            </p>
          )}
        </div>
      </div>

      {isArmB ? null : (
        <>
          <p className={css.sub}>
            <FormattedMessage
              id="CheckoutHandoffModal.cost"
              values={{
                count: idleSlots,
                ordinal: NEXT_HORSE_ORDINAL[idleSlots] || '',
              }}
            />
          </p>
          {/* 🔴 RE-REVIEW FR-4 — the readout is SUPPRESSED when there is no
              tier breakdown to name.

              FIX-3 gave the inconclusive leg an honest count from the mirror
              but deliberately left `tierBreakdown: []`, because tiers really
              are unknown when the authority never answered. The readout was
              still rendered unconditionally, and `tierBreakdownLabel([], n)`
              returns `''`, so the line came out as a subject-less fragment:

                  " — paid, ready to use · no horses posted to them yet"

              …on EVERY dead-endpoint ask, i.e. the whole leg FIX-3 exists for.
              The readout's entire job (AC2.6) is to name a tier and a count;
              with no tiers to name it has nothing to say, so it says nothing.
              The count still appears in the headline above. */}
          {breakdownLabel ? (
            <p className={css.tierName}>
              <FormattedMessage
                id="CheckoutHandoffModal.readout"
                values={{ count: idleSlots, breakdown: breakdownLabel }}
              />
            </p>
          ) : null}
        </>
      )}

      <div className={css.cards}>
        {isArmB ? (
          <>
            <button
              type="button"
              className={`${css.btn} ${css.btnGold}`}
              onClick={handleUpdatePayment}
              disabled={portalInProgress}
            >
              {intl.formatMessage({ id: 'CheckoutHandoffModal.armB.updatePayment' })}
            </button>
            {/* 🔴 COLD-REVIEW SF-6 / DEV-8 — ARM B MUST NOT STRIP THE WIZARD.
                Arm B outranks Arm A when BOTH hold, and that ordering is right:
                the billing problem is the one requiring action. But the screen
                rendered ONLY [Update payment], so a seller holding a spendable
                listing AND a stale past_due row was told their card failed and
                given no way to use the listing they had already paid for — and
                no way to buy either. Doors 3/5 were shut on them until billing
                was fixed, which for `unpaid` (dunning exhausted) means until an
                operator intervenes.

                AC4.2 forbids a "purchase a listing" CTA on this state. It does
                NOT forbid the route to the listing they already own, and that
                route costs nothing and charges nothing. Shown ONLY when the
                authority actually reported a spendable listing, so the
                billing-only case is unchanged. */}
            {spendableSlots > 0 ? (
              <span onClick={handleUseExisting} className={css.cardsSingle}>
                <NamedLink name="NewListingPage" className={`${css.btn} ${css.btnOut}`}>
                  <FormattedMessage
                    id="CheckoutHandoffModal.usePaid"
                    values={{ count: spendableSlots }}
                  />
                </NamedLink>
              </span>
            ) : null}
          </>
        ) : (
          <>
            {/* ⚠️ NamedLink does NOT forward onClick — it destructures a fixed
                prop whitelist and rebuilds the <a> props (NamedLink.js:57-105),
                so a handler passed to it is silently dropped. The outcome is
                therefore recorded on a wrapper that catches the BUBBLED click.
                Keeping NamedLink matters: this is a real in-app navigation to
                NewListingPage and must stay a routed link, not a button. */}
            <span onClick={handleUseExisting} className={css.cardsSingle}>
              <NamedLink name="NewListingPage" className={`${css.btn} ${css.btnGold}`}>
                <FormattedMessage id="CheckoutHandoffModal.usePaid" values={{ count: idleSlots }} />
              </NamedLink>
            </span>
            <button type="button" className={`${css.btn} ${css.btnOut}`} onClick={handleBuyAnother}>
              <FormattedMessage
                id="CheckoutHandoffModal.buyAnother"
                values={{ count: idleSlots }}
              />
            </button>
          </>
        )}
      </div>

      {/* Reuses BillingSubscriptionsPage's existing portal-error string rather
          than minting a second one — same failure, same surface, same words.
          No new copy was invented here; P-3's approved block covers the happy
          path only. */}
      {portalError ? (
        <p className={css.ctaError}>
          <FormattedMessage id="BillingSubscriptionsPage.portalError" />
        </p>
      ) : null}

      <p className={css.fine}>
        {isArmB ? (
          <FormattedMessage id="CheckoutHandoffModal.armB.footnote" />
        ) : (
          <FormattedMessage id="CheckoutHandoffModal.footnote" values={{ count: idleSlots }} />
        )}
      </p>
    </Modal>
  );
};

CheckoutHandoffModal.propTypes = {
  handoff: object,
  onClose: func.isRequired,
  onOutcome: func,
  navigateExternal: func,
};

export default CheckoutHandoffModal;
